// Layers context — centralized layer-visibility state (US-875 · AC-3/AC-7).
//
// AC-7 requires map/layer state to be centralized (context + top-level ref), not
// scattered across components. US-874 deliberately kept MapContext map-instance
// ONLY, so layer state lives in this dedicated context instead of bloating it.
// It consumes the `mapRef` from MapContext and owns:
//   - `visibleLayers`: { [layerId]: boolean } — the single source of on/off truth
//   - `toggleLayer(id)`: flips a layer plus its coupled (China) companions
// A single batched effect mirrors that state onto the live map via
// `setLayoutProperty` (no remove/re-add → flicker-free, AC-3). Consume via
// the `useLayers` hook.
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import useMap from "../hooks/useMap";
import {
  CHN_RASTER_TWINS,
  DEFAULT_GAP_STYLE,
  GAP_STYLE_RASTER,
  GAP_STYLE_VECTOR,
  LAYERS,
  RASTER_OPACITY_BY_ZOOM,
  initialVisibility,
  layerWithCoupled,
} from "../config/layerConfig";

export const LayersContext = createContext(null);

// Guarded visibility setter — David's style may not contain every configured id,
// so skip absent layers instead of throwing (design.md §4, robustness/T7).
const safeSetVisibility = (map, id, visible) => {
  if (map.getLayer && map.getLayer(id)) {
    map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
  }
};

// Raster gap layers toggle via raster-opacity, never via visibility — a
// hidden raster layer's band chunks are skipped at tile load, and a
// visibility flip either never refetches them (GL 3.18, renders blank) or
// costs a network round-trip (3.25). The ON value is the zoom-graduated
// expression (A-8): full at regional zooms, translucent when zoomed in so
// the basemap stays legible. See layerConfig.
const safeSetRasterOpacity = (map, id, on) => {
  if (map.getLayer && map.getLayer(id) && map.setPaintProperty) {
    map.setPaintProperty(id, "raster-opacity", on ? RASTER_OPACITY_BY_ZOOM : 0);
  }
};

export const LayersProvider = ({ children }) => {
  const { mapRef, isMapReady } = useMap();
  const [visibleLayers, setVisibleLayers] = useState(initialVisibility);
  // US-920 (A-2): which family the gap toggles paint — continuous raster
  // (default) or the legacy vector cells. One switch, comparison-ready.
  const [gapStyle, setGapStyle] = useState(DEFAULT_GAP_STYLE);

  // Toggle a layer and its coupled companions together (legacy coupledLayerMap).
  // US-922 (R-5): toggling NEVER moves the camera — the old anchor auto-zoom
  // (US-920 R-12) is retired: the cluster tiers render anchors at every zoom,
  // so forcing the frame to z6 went from help to hindrance.
  const toggleLayer = useCallback((layerId) => {
    const layer = LAYERS.find((l) => l.id === layerId);
    if (!layer) return;
    setVisibleLayers((prev) => {
      const next = !prev[layerId];
      const updated = { ...prev };
      // A-9: toggles in an exclusive group behave like radios — activating
      // one deactivates its siblings first. Siblings drop their coupled
      // companions too; a companion SHARED with the activated layer
      // (urban-gap-china) is re-set below, so it survives the swap.
      if (next && layer.exclusiveGroup) {
        LAYERS.filter(
          (l) => l.exclusiveGroup === layer.exclusiveGroup && l.id !== layer.id
        ).forEach((sibling) => {
          layerWithCoupled(sibling).forEach((id) => {
            updated[id] = false;
          });
        });
      }
      layerWithCoupled(layer).forEach((id) => {
        updated[id] = next;
      });
      return updated;
    });
  }, []);

  // Set explicit visibility for a batch of layers ({ [id]: boolean }). Used by
  // the borders-follow-level coupling (US-878 UX) so the admin boundary layers
  // track the active selection level instead of standalone toggles. Returns the
  // previous state untouched when nothing changes (no spurious re-render).
  const setLayersVisible = useCallback((updates) => {
    setVisibleLayers((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(updates).forEach(([id, visible]) => {
        if (next[id] !== visible) {
          next[id] = visible;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  // Mirror state → map. Runs once the map is ready and on every state change.
  // Applying the whole inventory each time is idempotent and keeps the map in
  // lockstep with state without diffing; setLayoutProperty never re-creates a
  // source, so pan/zoom performance is preserved (AC-8) and there is no flicker.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    LAYERS.forEach((layer) => {
      // US-920 (A-2): gap toggles paint the family the gap-style switch
      // selects. The raster layer stays layout-visible and toggles via
      // OPACITY (band-chunk lifecycle, see safeSetRasterOpacity); the vector
      // layer toggles via visibility only in Cells mode; China companions
      // (vector, mode-agnostic) follow their own coupled state.
      if (layer.vectorId) {
        const on = visibleLayers[layer.id];
        safeSetRasterOpacity(map, layer.id, gapStyle === GAP_STYLE_RASTER && on);
        safeSetVisibility(map, layer.vectorId, gapStyle === GAP_STYLE_VECTOR && on);
        // R-11: the vector China companions are the CELLS-mode rendering of
        // China only — in Continuous mode the chn_* raster twins paint China
        // (aggregate vector blocks over the continuous surface read as a bug).
        layer.coupledIds.forEach((id) => {
          safeSetVisibility(map, id, gapStyle === GAP_STYLE_VECTOR && visibleLayers[id]);
        });
        return;
      }
      layerWithCoupled(layer).forEach((id) => {
        safeSetVisibility(map, id, visibleLayers[id]);
      });
    });
    // R-11: each China twin follows its parent toggles with OR semantics —
    // China has ONE fiber band, so either fiber toggle lights the fiber twin.
    // Twins render only in Continuous mode and, like every raster gap layer,
    // toggle via opacity (band-chunk lifecycle).
    CHN_RASTER_TWINS.forEach((twin) => {
      const anyParentOn = twin.parents.some((id) => visibleLayers[id]);
      safeSetRasterOpacity(map, twin.id, gapStyle === GAP_STYLE_RASTER && anyParentOn);
    });
    // Notify non-React listeners (legacy hooks, future consumers) that
    // visibility changed; React consumers read `visibleLayers` directly.
    window.dispatchEvent(new Event("map-layer-visibility-change"));
  }, [mapRef, isMapReady, visibleLayers, gapStyle]);

  const value = useMemo(
    () => ({ visibleLayers, toggleLayer, setLayersVisible, gapStyle, setGapStyle }),
    [visibleLayers, toggleLayer, setLayersVisible, gapStyle]
  );

  return (
    <LayersContext.Provider value={value}>{children}</LayersContext.Provider>
  );
};
